/**
 * SyncQueueBadge — pinned in the admin layout topbar.
 *
 * Shows a pulsing yellow badge whenever there are pending offline writes
 * (created products, credits, stock changes...) waiting to upload. One
 * click → opens /admin/sync-queue where the merchant can see each
 * pending item and manually retry / discard.
 */
import React, { useEffect, useState } from "react";
import { tr } from '@/i18n/tr';
import { Link } from "react-router-dom";
import { CloudOff, Cloud } from "lucide-react";
import { toast } from "sonner";
import { subscribeQueue } from "@/utils/syncQueue";
export default function SyncQueueBadge() {
  const [count, setCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => {
    const off = subscribeQueue((items) => {
      setCount(items.length);
      setFailedCount(items.filter((it) => it.permanent_fail).length);
    });
    const goOn = () => setOnline(true);
    const goOff = () => setOnline(false);
    window.addEventListener("online", goOn);
    window.addEventListener("offline", goOff);
    return () => {
      off();
      window.removeEventListener("online", goOn);
      window.removeEventListener("offline", goOff);
    };
  }, []);

  if (count === 0 && online) return null;

  return (
    <Link
      to="/admin/sync-queue"
      data-testid="sync-queue-badge"
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${
        !online || failedCount > 0
          ? "bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse"
          : count > 0
          ? "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40 animate-pulse"
          : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
      }`}
      title={!online ? "Sin internet" : failedCount > 0 ? `${failedCount} ${tr('x_1f06712c')}` : count > 0 ? `${count} pendientes de sincronizar` : "Sincronizado"}
    >
      {!online ? <CloudOff className="w-3.5 h-3.5" /> : <Cloud className="w-3.5 h-3.5" />}
      <span>{!online ? "OFFLINE" : failedCount > 0 ? `⚠️ ${failedCount} ${tr('x_acd5cb84')}` : `${count} ${tr('x_a0bf20e8')}`}</span>
    </Link>
  );
}
